import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Translate } from 'react-localize-redux';
import { useQuery } from '@apollo/client';
import { useFormikContext } from 'formik-2';
import { partnerShape } from '../types';
import { TENCENT_PARTNER_NAME, TWITTER_PARTNER_NAME } from '../../consts';
import css from './PartnerConfigurationShelf.css';
import partnerAdAccountCss from './PartnerAdAccount.css';
import { Spinner, AutoCompleteField } from '../../../components/widgets';
import { GET_PARTNER_ACCOUNTS } from '../../queries';

export const PARTNER_ACCOUNT_DROPDOWN_TEST_ID = 'partner-account-dropdown';

export const getPartnerAdAccountValidationSchema = partner =>
    partner?.accountsSupport
        ? {
              accounts: Yup.array()
                  .min(1, 'STATIC.REQUIRED_FIELD')
                  .required('STATIC.REQUIRED_FIELD'),
          }
        : {};

const DEFAULT_PARTNER_CONFIG = {
    dropdownHeader: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.DROPDOWN_HEADER_DEFAULT',
    extraDisplayFields: [],
    multiSelect: false,
};

const PARTNER_CONFIGS = {
    [TENCENT_PARTNER_NAME]: {
        dropdownHeader: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.DROPDOWN_HEADER_TENCENT',
        extraDisplayFields: ['qqNumber'],
    },
    [TWITTER_PARTNER_NAME]: {
        multiSelect: true,
    },
};

const getPartnerConfig = partnerName => ({
    ...DEFAULT_PARTNER_CONFIG,
    ...PARTNER_CONFIGS[partnerName],
});

const getAvailableAccounts = partnerAccounts =>
    (partnerAccounts ?? [])
        .filter(({ loginError }) => !loginError)
        .map(({ loginError, loginAccounts, ...account }) => ({
            ...account,
            loginAccounts: (loginAccounts || []).filter(la => la.enabled),
        }));

// Only get logins available in the "Get All Accounts" endpoint
const filterValidLogins = (selected, available) =>
    (selected || []).filter(a => available.some(acc => acc.loginId === a.loginId));

// Only get login accounts available in the "Get All Accounts" endpoint
const filterValidLoginAccounts = (selected, available) =>
    (selected || [])
        .map(login => ({
            ...login,
            loginAccounts: (login.loginAccounts || []).filter(la => available.some(a => a.accountId === la.accountId)),
        }))
        .filter(login => login.loginAccounts.length > 0);

const formatDropdownLabel = (account, partnerConfig) => {
    const loginData = account.loginData?.[0] || {};
    const extraValues = partnerConfig.extraDisplayFields.map(field => loginData[field]).filter(Boolean);
    if (extraValues.length === 0) return account.loginId;
    return [account.loginId, ...extraValues].join(' - ');
};

function getSingleDropdownProps(availableLogins, partnerConfig, selectedLogins, setFieldValue, setFieldTouched) {
    const options = availableLogins.map(account => ({
        value: account.loginId,
        label: formatDropdownLabel(account, partnerConfig),
    }));

    const groupBy = () => ({ [partnerConfig.dropdownHeader]: options });

    const value = filterValidLogins(selectedLogins, availableLogins).map(a => ({
        value: a.loginId,
        label: formatDropdownLabel(a, partnerConfig),
    }));

    const onChange = option => {
        setFieldTouched('accounts', true);
        if (!option) {
            setFieldValue('accounts', []);
            return;
        }
        const account = availableLogins.find(acc => acc.loginId === option.value);
        setFieldValue('accounts', account ? [account] : []);
    };

    return {
        options,
        groupBy,
        value,
        onChange,
        isMulti: false,
        containerClass: partnerAdAccountCss.dropdownContainerSingle,
    };
}

const compositeKey = (loginId, accountId) => `${loginId}::${accountId}`;

function getMultiDropdownProps(
    availableLogins,
    availableLoginAccounts,
    selectedAccounts,
    setFieldValue,
    setFieldTouched
) {
    const options = availableLoginAccounts.map(la => ({
        value: compositeKey(la.loginId, la.accountId),
        label: la.accountName,
        loginId: la.loginId,
    }));

    const groupBy = filteredOptions =>
        filteredOptions.reduce((groups, option) => {
            const group = groups[option.loginId] || [];
            return { ...groups, [option.loginId]: [...group, option] };
        }, {});

    const value = filterValidLoginAccounts(selectedAccounts, availableLoginAccounts).flatMap(login =>
        login.loginAccounts.map(la => ({
            value: compositeKey(login.loginId, la.accountId),
            label: la.accountName,
        }))
    );

    const accountByKey = Object.fromEntries(
        availableLoginAccounts.map(la => [compositeKey(la.loginId, la.accountId), la])
    );
    const loginById = Object.fromEntries(availableLogins.map(a => [a.loginId, a]));

    const onChange = option => {
        setFieldTouched('accounts', true);
        if (!option || option.length === 0) {
            setFieldValue('accounts', []);
            return;
        }

        const selectedByLogin = option.reduce((groups, o) => {
            const la = accountByKey[o.value];
            if (!la) return groups;
            return { ...groups, [la.loginId]: [...(groups[la.loginId] || []), la.accountId] };
        }, {});

        const selected = Object.entries(selectedByLogin).map(([loginId, selectedIds]) => {
            const login = loginById[loginId];
            const selectedSet = new Set(selectedIds);
            return {
                ...login,
                loginAccounts: (login.loginAccounts || []).filter(la => selectedSet.has(la.accountId)),
            };
        });
        setFieldValue('accounts', selected);
    };

    return {
        options,
        groupBy,
        value,
        onChange,
        isMulti: true,
        selectableGroupHeaders: true,
        containerClass: partnerAdAccountCss.dropdownContainerMulti,
        size: 'xlarge',
    };
}

function PartnerAdAccount({ partner, isReadonly = false }) {
    const { data, loading } = useQuery(GET_PARTNER_ACCOUNTS, {
        variables: { partnerId: partner.id },
    });
    const availableLogins = useMemo(() => getAvailableAccounts(data?.partnerAccounts), [data?.partnerAccounts]);
    const partnerConfig = useMemo(() => getPartnerConfig(partner.name), [partner.name]);
    const { values, setFieldValue, setFieldTouched, errors, touched } = useFormikContext();

    const availableLoginAccounts = useMemo(
        () =>
            availableLogins.flatMap(login =>
                (login.loginAccounts || []).map(la => ({ ...la, loginId: login.loginId }))
            ),
        [availableLogins]
    );

    useEffect(() => {
        if (loading || !values.accounts?.length) return;
        const valid = partnerConfig.multiSelect
            ? filterValidLoginAccounts(values.accounts, availableLoginAccounts)
            : filterValidLogins(values.accounts, availableLogins);
        if (valid.length !== values.accounts.length) setFieldValue('accounts', valid);
    }, [loading, availableLoginAccounts, availableLogins, values.accounts, setFieldValue, partnerConfig.multiSelect]);

    if (loading) return <Spinner show expanded />;

    const {
        options,
        groupBy,
        value,
        onChange,
        isMulti,
        selectableGroupHeaders,
        containerClass,
        size,
    } = partnerConfig.multiSelect
        ? getMultiDropdownProps(
              availableLogins,
              availableLoginAccounts,
              values.accounts,
              setFieldValue,
              setFieldTouched
          )
        : getSingleDropdownProps(availableLogins, partnerConfig, values.accounts, setFieldValue, setFieldTouched);

    const accountsError = touched.accounts && errors.accounts;

    return (
        <div className={css.section} data-field-error={accountsError ? 'accounts' : undefined}>
            <div className={css.title}>
                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.TITLE" />
            </div>
            <div className={css.subtitle}>
                <Translate
                    id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.SUBTITLE"
                    data={{ partnerName: partner.displayName }}
                />
            </div>
            <AutoCompleteField
                dataTestId={PARTNER_ACCOUNT_DROPDOWN_TEST_ID}
                label="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.TITLE"
                containerClass={containerClass}
                disabled={isReadonly}
                error={accountsError}
                clearable
                advanced
                options={options}
                groupBy={groupBy}
                value={value}
                onChange={onChange}
                isMulti={isMulti}
                selectableGroupHeaders={selectableGroupHeaders}
                size={size}
            />
        </div>
    );
}

PartnerAdAccount.propTypes = {
    partner: partnerShape.isRequired,
    isReadonly: PropTypes.bool,
};

export default PartnerAdAccount;
